import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingBag as ShoppingBagIcon
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';

const Cart = () => {
  const { cart, removeFromCart, updateQuantity } = useCart();
  const navigate = useNavigate();
  
  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  if (cart.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4, mt: { xs: 8, sm: 10 } }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            bgcolor: '#f8f8f8',
            borderRadius: 2
          }}
        >
          <ShoppingBagIcon 
            sx={{ 
              fontSize: 60, 
              color: 'text.secondary',
              mb: 2
            }} 
          />
          <Typography variant="h6" gutterBottom>
            Sepetiniz boş
          </Typography>
          <Typography color="text.secondary" gutterBottom>
            Alışverişe başlamak için ürünlerimize göz atın
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/products')}
            sx={{ mt: 3 }}
          >
            Alışverişe Başla
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ 
      py: 4,
      mt: { xs: '80px', sm: '90px' },
      minHeight: 'calc(100vh - 80px)',
      mb: 4
    }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        Sepetim ({cart.length} Ürün)
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ bgcolor: '#f8f8f8', borderRadius: 2 }}>
            <CardContent>
              {cart.map((item) => (
                <Box key={`${item.id}-${item.size}-${item.color}`}>
                  <Box sx={{ 
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 3,
                    py: 3
                  }}>
                    <Box sx={{ 
                      width: { xs: '100%', sm: 200 },
                      height: { xs: 240, sm: 200 },
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover img': {
                        transform: 'scale(1.05)',
                      }
                    }}>
                      <img
                        src={item.images?.[0] || '/placeholder.jpg'}
                        alt={item.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease'
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        mb: 2
                      }}>
                        <Typography variant="h6">
                          {item.title}
                        </Typography>
                        <IconButton 
                          onClick={() => removeFromCart(item.id, item.size, item.color)}
                          size="small"
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        {item.size && (
                          <Typography variant="body2" color="text.secondary">
                            Beden: {item.size}
                          </Typography>
                        )}
                        {item.color && (
                          <Typography variant="body2" color="text.secondary">
                            Renk: {item.color}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Box sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0.5
                        }}>
                          <IconButton 
                            size="small"
                            onClick={() => updateQuantity(item.id, item.size, item.color, Math.max(1, item.quantity - 1))}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography sx={{ mx: 2 }}>{item.quantity}</Typography>
                          <IconButton 
                            size="small"
                            onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {(item.price * item.quantity).toLocaleString('tr-TR')} TL
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ 
            bgcolor: '#f8f8f8', 
            borderRadius: 2,
            position: 'sticky',
            top: 100
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sipariş Özeti
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  mb: 2
                }}>
                  <Typography>Ara Toplam</Typography>
                  <Typography>{total.toLocaleString('tr-TR')} TL</Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  mb: 2
                }}>
                  <Typography>Kargo</Typography>
                  <Typography>Ücretsiz</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between'
                }}>
                  <Typography variant="h6">Toplam</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {total.toLocaleString('tr-TR')} TL
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => navigate('/checkout')}
                sx={{ 
                  py: 1.5,
                  borderRadius: 2,
                  mb: 2
                }}
              >
                Ödemeye Geç
              </Button>

              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={() => navigate('/products')}
                sx={{ 
                  py: 1.5,
                  borderRadius: 2
                }}
              >
                Alışverişe Devam Et
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Cart; 